import type {
  ApplyDecisionResult,
  BuildingCost,
  BattlePayload,
  Decision,
  EventPayload,
  ResourceType,
  RewardPayload,
  TerrainInfo,
  TileState,
  TriggeredEvent,
  VoteOption,
  WorldState,
} from "./types";

export interface InitStateConfig {
  roomId?: string;
  width?: number;
  height?: number;
  seed?: number;
  serverTimeMs?: number;
}

export const DEFAULT_MOVE_RANGE = 5;

function mulberry32(seed: number) {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function cloneState(state: WorldState): WorldState {
  return structuredClone(state) as WorldState;
}

export const REWARD_EVENTS: Omit<RewardPayload, "kind">[] = [
  { 
    text: "Дымная таверна! Эльфы-программисты курят кальян после тяжёлого дня кодинга.", 
    image: "/content/beer-tavern-1.png", 
    reward: { itemId: "hookah", itemName: "Кальян", description: "Приятный вечер в дыму!" } 
  },
  { 
    text: "Пивная таверна! Эльфы поднимают кружки за успешный деплой.", 
    image: "/content/beer-tavern-2.png", 
    reward: { itemId: "craft_beer", itemName: "Крафтовое пиво", description: "Угостит друзей!" } 
  },
];

export const BATTLE_EVENTS: Omit<BattlePayload, "kind">[] = [
  {
    text: "Гоблин выскакивает из-за дерева!",
    enemyTemplateId: "goblin",
    loot: { itemId: "goblin_ear", itemName: "Ухо гоблина", description: "Трофей.", cardRewards: ["cutting_blade", "battle_cry", "iron_defense"] },
  },
  {
    text: "Волк рычит из кустов!",
    enemyTemplateId: "wolf",
    loot: { itemId: "wolf_pelt", itemName: "Шкура волка", description: "Тёплая.", cardRewards: ["cutting_blade", "iron_defense"] },
  },
];

const RESOURCE_TYPES: ResourceType[] = ["gold", "mana"];


const INITIAL_HERO_RESOURCES: Record<ResourceType, number> = {
  gold: 5000,
  mana: 100,
};


const GOLD_PILE_OPTIONS = [500, 1000, 1500, 2000, 2500, 3000, 3500, 4000];


function pickResourceAmount(rand: () => number, resourceType: ResourceType): number {
  if (resourceType === "gold") {
    return GOLD_PILE_OPTIONS[Math.floor(rand() * GOLD_PILE_OPTIONS.length)];
  }
  return 10 + Math.floor(rand() * 30); 
}


export const RESOURCE_LABELS: Record<ResourceType, string> = {
  gold: "Золото",
  mana: "Мана",
};


export const RESOURCE_LABELS_GENITIVE: Record<ResourceType, string> = {
  gold: "золота",
  mana: "маны",
};


export function getResourcePileLabel(resourceType: ResourceType): string {
  return `Кучка ${RESOURCE_LABELS_GENITIVE[resourceType]}`;
}


export interface TownBuildingDef {
  id: string;
  displayName: string;
  
  description?: string;
  maxLevel: number;
  
  cost: BuildingCost;
  
  upgradeCost: BuildingCost;
}


export const TOWN_BUILDING_DEFS: TownBuildingDef[] = [
  {
    id: "hall",
    displayName: "Ратуша",
    description: "Увеличивает доход золотом и открывает новые опции найма.",
    maxLevel: 3,
    cost: { gold: 1000 },
    upgradeCost: { gold: 3000, mana: 20 },
  },
  {
    id: "barracks",
    displayName: "Казармы",
    description: "Позволяет нанимать и улучшать войска города.",
    maxLevel: 2,
    cost: { gold: 1500, mana: 30 },
    upgradeCost: { gold: 2500, mana: 50 },
  },
];

function pickEventPayload(rand: () => number, eventIndex: number): EventPayload {
  if (eventIndex % 4 === 3) {
    const battle = BATTLE_EVENTS[Math.floor(rand() * BATTLE_EVENTS.length)];
    return { kind: "battle", ...battle };
  }
  const reward = REWARD_EVENTS[eventIndex % REWARD_EVENTS.length];
  return { kind: "reward", ...reward };
}

function pickGrassDecoration(rand: () => number) {
  const r = rand();
  if (r < 0.10) return "flower";
  if (r < 0.18) return "bush";
  if (r < 0.23) return "rock";
  return undefined;
}

function makeForest(): TerrainInfo {
  return { type: "forest", passable: false, decoration: "tree" };
}

function setRoadTile(tile: TileState) {
  tile.terrain = { type: "road", passable: true };
}

function setGrassTile(tile: TileState, rand: () => number) {
  tile.terrain = { type: "grass", passable: true, decoration: pickGrassDecoration(rand) };
}

function carveRoadPath(tiles: TileState[][], fromX: number, fromY: number, toX: number, toY: number, rand: () => number) {
  let x = fromX;
  let y = fromY;
  const maxSteps = tiles.length * tiles[0].length;

  if (tiles[y]?.[x]) setRoadTile(tiles[y][x]);

  for (let i = 0; i < maxSteps && (x !== toX || y !== toY); i++) {
    const dx = x !== toX ? Math.sign(toX - x) : 0;
    const dy = y !== toY ? Math.sign(toY - y) : 0;
    
    x += dx;
    y += dy;

    if (!tiles[y]?.[x]) break;
    setRoadTile(tiles[y][x]);
    
    if (dx !== 0 && dy !== 0) {
      const adj1 = tiles[y - dy]?.[x]; 
      const adj2 = tiles[y]?.[x - dx]; 
      if (adj1 && adj1.terrain.type === "forest" && adj1.content.kind === "empty") {
        setGrassTile(adj1, rand);
      }
      if (adj2 && adj2.terrain.type === "forest" && adj2.content.kind === "empty") {
        setGrassTile(adj2, rand);
      }
    }
  }
}


function placeLakes(
  tiles: TileState[][],
  width: number,
  height: number,
  heroX: number,
  heroY: number,
  rand: () => number,
) {
  const targetLakes = 3 + Math.floor(rand() * 2); 
  let placed = 0;
  let attempts = 0;
  while (placed < targetLakes && attempts < 120) {
    attempts++;
    const radius = 2 + Math.floor(rand() * 2); 
    const sandRadius = radius + 1.5;
    const margin = Math.ceil(sandRadius) + 2;
    const cx = margin + Math.floor(rand() * (width - margin * 2));
    const cy = margin + Math.floor(rand() * (height - margin * 2));

    if (Math.max(Math.abs(cx - heroX), Math.abs(cy - heroY)) < 8) continue;

    let canPlace = true;
    const r2 = sandRadius * sandRadius;
    for (let dy = -Math.ceil(sandRadius); dy <= Math.ceil(sandRadius) && canPlace; dy++) {
      for (let dx = -Math.ceil(sandRadius); dx <= Math.ceil(sandRadius); dx++) {
        if (dx * dx + dy * dy > r2) continue;
        const t = tiles[cy + dy]?.[cx + dx];
        if (!t || t.terrain.type !== "forest" || t.content.kind !== "empty") {
          canPlace = false; break;
        }
      }
    }
    if (!canPlace) continue;

    const waterR2 = radius * radius;
    for (let dy = -Math.ceil(sandRadius); dy <= Math.ceil(sandRadius); dy++) {
      for (let dx = -Math.ceil(sandRadius); dx <= Math.ceil(sandRadius); dx++) {
        const d2 = dx * dx + dy * dy;
        if (d2 > r2) continue;
        const t = tiles[cy + dy][cx + dx];
        if (d2 <= waterR2) {
          t.terrain = { type: "water", passable: false };
        } else {
          t.terrain = { type: "sand", passable: true };
        }
      }
    }

    carveSandPathToRoad(tiles, width, height, cx, cy, Math.ceil(sandRadius));
    placed++;
  }
}


function carveSandPathToRoad(
  tiles: TileState[][],
  width: number,
  height: number,
  lakeX: number,
  lakeY: number,
  searchRadius: number,
) {
  const MAX_DIST = 25;
  let bestRoad: { x: number; y: number; dist: number } | null = null;
  for (let dy = -MAX_DIST; dy <= MAX_DIST; dy++) {
    for (let dx = -MAX_DIST; dx <= MAX_DIST; dx++) {
      const x = lakeX + dx;
      const y = lakeY + dy;
      if (x < 0 || x >= width || y < 0 || y >= height) continue;
      if (tiles[y][x].terrain.type !== "road") continue;
      const dist = Math.max(Math.abs(dx), Math.abs(dy)); 
      if (!bestRoad || dist < bestRoad.dist) bestRoad = { x, y, dist };
    }
  }
  if (!bestRoad || bestRoad.dist <= searchRadius) return; 

  let x = lakeX, y = lakeY;
  for (let i = 0; i < MAX_DIST + 5; i++) {
    if (x === bestRoad.x && y === bestRoad.y) break;
    const dx = x !== bestRoad.x ? Math.sign(bestRoad.x - x) : 0;
    const dy = y !== bestRoad.y ? Math.sign(bestRoad.y - y) : 0;
    x += dx;
    y += dy;
    const t = tiles[y]?.[x];
    if (!t) break;
    if (t.terrain.type !== "forest") continue;
    if (t.content.kind !== "empty") continue;
    t.terrain = { type: "sand", passable: true };
  }
}

function clearAroundNode(tiles: TileState[][], x: number, y: number, rand: () => number) {
  const neighbors = [
    { dx: 1, dy: 0 }, { dx: -1, dy: 0 },
    { dx: 0, dy: 1 }, { dx: 0, dy: -1 },
    { dx: 1, dy: 1 }, { dx: 1, dy: -1 },
    { dx: -1, dy: 1 }, { dx: -1, dy: -1 },
  ];
  
  for (const n of neighbors) {
    const nx = x + n.dx;
    const ny = y + n.dy;
    const tile = tiles[ny]?.[nx];
    if (tile && tile.terrain.type === "forest" && tile.content.kind === "empty") {
      setGrassTile(tile, rand);
    }
  }
}

const MAP_ALGORITHM_VERSION = 10;

const mapCache = new Map<string, WorldState>();

export function clearMapCache(): void {
  mapCache.clear();
}

export function initState(config: InitStateConfig = {}): WorldState {
  const width = config.width ?? 100;
  const height = config.height ?? 100;
  const roomId = config.roomId ?? "global";
  const seed = config.seed ?? 1337;
  const serverTimeMs = config.serverTimeMs ?? Date.now();

  const cacheKey = `${seed}-v${MAP_ALGORITHM_VERSION}`;

  const defaultTownId = "town-main";
  if (mapCache.has(cacheKey)) {
    const cached = cloneState(mapCache.get(cacheKey)!);
    cached.roomId = roomId;
    cached.serverTimeMs = serverTimeMs;
    if (!cached.towns.byId[defaultTownId]) {
      cached.towns.byId[defaultTownId] = {
        id: defaultTownId,
        name: "Главный город",
        level: 1,
        buildings: {},
      };
      if (!cached.towns.discoveredIds.includes(defaultTownId)) cached.towns.discoveredIds.push(defaultTownId);
    }
    return cached;
  }

  const rand = mulberry32(seed);

  const tiles: TileState[][] = [];
  for (let y = 0; y < height; y++) {
    const row: TileState[] = [];
    for (let x = 0; x < width; x++) {
      row.push({
        x, y,
        terrain: makeForest(),
        fog: { discovered: false, visited: false },
        content: { kind: "empty" },
      });
    }
    tiles.push(row);
  }

  const heroX = Math.floor(width / 2);
  const heroY = Math.floor(height / 2);
  const visionRadius = 8; 

  const DIRECTIONS = [
    { dx: 0, dy: -1 },   
    { dx: 1, dy: -1 },   
    { dx: 1, dy: 0 },    
    { dx: 1, dy: 1 },    
    { dx: 0, dy: 1 },    
    { dx: -1, dy: 1 },   
    { dx: -1, dy: 0 },   
    { dx: -1, dy: -1 },  
  ];

  interface TreeNode {
    x: number;
    y: number;
    depth: number;
  }

  const nodes: TreeNode[] = [];
  const eventsById: WorldState["events"]["byId"] = {};
  const resourcesById: WorldState["resources"]["byId"] = {};
  let evIdx = 0;
  let resIdx = 0;
  
  const EVENT_CHANCE = 0.2;

  const EDGE_LENGTH = 5;
  const MAX_DEPTH = 5;

  const isOccupied = (x: number, y: number): boolean => {
    if (x < 5 || x >= width - 5 || y < 5 || y >= height - 5) return true;
    for (const node of nodes) {
      if (Math.max(Math.abs(x - node.x), Math.abs(y - node.y)) < 4) return true;
    }
    return false;
  };

  nodes.push({ x: heroX, y: heroY, depth: 0 });
  setRoadTile(tiles[heroY][heroX]);
  clearAroundNode(tiles, heroX, heroY, rand);

  const queue: Array<{ node: TreeNode; fromDir: number }> = [{ node: nodes[0], fromDir: -1 }];

  while (queue.length > 0) {
    const { node: parent, fromDir } = queue.shift()!;
    if (parent.depth >= MAX_DEPTH) continue;

    const numChildren = 2 + Math.floor(rand() * 2); 

    const oppositeDir = fromDir === -1 ? -1 : (fromDir + 4) % 8;
    const availableDirs = DIRECTIONS
      .map((_, i) => i)
      .filter(i => i !== oppositeDir); 

    for (let i = availableDirs.length - 1; i > 0; i--) {
      const j = Math.floor(rand() * (i + 1));
      [availableDirs[i], availableDirs[j]] = [availableDirs[j], availableDirs[i]];
    }

    let childrenPlaced = 0;
    for (const dirIdx of availableDirs) {
      if (childrenPlaced >= numChildren) break;

      const dir = DIRECTIONS[dirIdx];
      const childX = parent.x + dir.dx * EDGE_LENGTH;
      const childY = parent.y + dir.dy * EDGE_LENGTH;

      if (isOccupied(childX, childY)) continue;

      const child: TreeNode = { x: childX, y: childY, depth: parent.depth + 1 };
      nodes.push(child);
      queue.push({ node: child, fromDir: dirIdx });
      childrenPlaced++;

      carveRoadPath(tiles, parent.x, parent.y, childX, childY, rand);
      setRoadTile(tiles[childY][childX]);
      
      clearAroundNode(tiles, childX, childY, rand);

      const forceBattle = child.depth === 1 && evIdx < 2;
      if (forceBattle || rand() < EVENT_CHANCE) {
        const eventId = `event-${evIdx + 1}`;
        const payload = forceBattle
          ? { kind: "battle" as const, ...BATTLE_EVENTS[evIdx % BATTLE_EVENTS.length] }
          : pickEventPayload(rand, evIdx);
        tiles[childY][childX].content = { kind: "event", eventId };
        eventsById[eventId] = { id: eventId, triggered: false, payload };
        evIdx++;
      } else {
        const resourceType = RESOURCE_TYPES[Math.floor(rand() * RESOURCE_TYPES.length)];
        const amount = pickResourceAmount(rand, resourceType);
        const resourceId = `resource-${resIdx + 1}`;
        resourcesById[resourceId] = { id: resourceId, resourceType, amount };
        tiles[childY][childX].content = { kind: "resource", resourceId };
        resIdx++;
      }
    }
  }

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (tiles[y][x].terrain.type !== "road") continue;
      for (const n of [{ dx: 1, dy: 0 }, { dx: -1, dy: 0 }, { dx: 0, dy: 1 }, { dx: 0, dy: -1 }]) {
        const nx = x + n.dx;
        const ny = y + n.dy;
        if (!tiles[ny]?.[nx]) continue;
        if (tiles[ny][nx].terrain.type === "forest" && rand() < 0.15) {
          setGrassTile(tiles[ny][nx], rand);
        }
      }
    }
  }

  placeLakes(tiles, width, height, heroX, heroY, rand);

  const townsById: WorldState["towns"]["byId"] = {};
  townsById[defaultTownId] = {
    id: defaultTownId,
    name: "Главный город",
    level: 1,
    buildings: {},
  };

  const state: WorldState = {
    version: 1, roomId, turn: 0, serverTimeMs,
    hero: {
      id: "hero-1", x: heroX, y: heroY, visionRadius,
      inventory: { items: [], resources: { ...INITIAL_HERO_RESOURCES } },
      deck: [
        "cutting_blade", "cutting_blade", "cutting_blade",
        "battle_cry", "iron_defense",
        "basic_strike", "basic_defend",
      ],
      collection: [
        "cutting_blade", "cutting_blade", "cutting_blade",
        "battle_cry", "iron_defense",
        "basic_strike", "basic_defend",
      ],
      flags: {},
      classId: null,
      domainId: null,
      baseStats: { strength: 1, dexterity: 1, intelligence: 1, wisdom: 1, constitution: 1 },
      stats: { strength: 1, dexterity: 1, intelligence: 1, wisdom: 1, constitution: 1 },
      hp: 30,
      maxHp: 30,
      armor: 2,
      evasion: 12,
      equipment: { head: null, body: null, hands: null, weapon: null, accessory: null },
      fearAccumulated: 0,
      xp: 0,
      level: 1,
      reputation: 0,
      reputationUpgrades: {},
      difficulty: "normal",
    },
    map: { width, height, tiles },
    towns: { byId: townsById, discoveredIds: [defaultTownId] },
    events: { byId: eventsById, triggeredIds: [] },
    resources: { byId: resourcesById, collectedIds: [] },
  };

  const result = advanceFog(state);
  
  mapCache.set(cacheKey, cloneState(result));
  
  return result;
}

export function listVoteOptions(_state: WorldState): VoteOption[] {
  const opts: VoteOption[] = [];
  opts.push({ kind: "action", id: "action:stay", action: "stay", label: "Остаться на месте" });
  return opts;
}

export function isValidTarget(state: WorldState, targetX: number, targetY: number, range: number = DEFAULT_MOVE_RANGE): { valid: boolean; reason?: string } {
  const { width, height, tiles } = state.map;
  if (targetX < 0 || targetX >= width || targetY < 0 || targetY >= height) return { valid: false, reason: "Вне карты" };
  if (targetX === state.hero.x && targetY === state.hero.y) return { valid: false, reason: "Уже здесь" };
  const tile = tiles[targetY][targetX];
  if (!tile.fog.discovered) return { valid: false, reason: "Не исследовано" };
  if (!tile.terrain.passable) return { valid: false, reason: "Непроходимо" };
  const path = findPath(state, state.hero.x, state.hero.y, targetX, targetY, range);
  if (!path) return { valid: false, reason: "Нет пути" };
  return { valid: true };
}

export function findPath(state: WorldState, sx: number, sy: number, ex: number, ey: number, maxDist: number): { x: number; y: number }[] | null {
  const { width, height, tiles } = state.map;
  if (sx === ex && sy === ey) return [];
  const visited = new Set<string>();
  const queue: { x: number; y: number; path: { x: number; y: number }[] }[] = [{ x: sx, y: sy, path: [] }];
  visited.add(`${sx},${sy}`);
  
  const dirs = [
    { dx: 1, dy: 0 },   
    { dx: -1, dy: 0 },  
    { dx: 0, dy: 1 },   
    { dx: 0, dy: -1 },  
    { dx: 1, dy: 1 },   
    { dx: 1, dy: -1 },  
    { dx: -1, dy: 1 },  
    { dx: -1, dy: -1 }, 
  ];
  
  while (queue.length > 0) {
    const cur = queue.shift()!;
    for (const d of dirs) {
      const nx = cur.x + d.dx, ny = cur.y + d.dy, key = `${nx},${ny}`;
      if (nx < 0 || nx >= width || ny < 0 || ny >= height || visited.has(key)) continue;
      
      if (!tiles[ny][nx].terrain.passable) continue;
      const newPath = [...cur.path, { x: nx, y: ny }];
      if (newPath.length > maxDist) continue;
      if (nx === ex && ny === ey) return newPath;
      visited.add(key);
      queue.push({ x: nx, y: ny, path: newPath });
    }
  }
  return null;
}

export function applyDecision(state: WorldState, decision: Decision): ApplyDecisionResult {
  const next = cloneState(state);
  next.serverTimeMs = Date.now();
  const triggered: TriggeredEvent[] = [];

  if (decision.kind === "stay") { next.turn += 1; return { state: next, triggeredEvents: triggered }; }

  if (decision.kind === "target") {
    const path = findPath(next, next.hero.x, next.hero.y, decision.x, decision.y, DEFAULT_MOVE_RANGE);
    if (path) {
      for (const step of path) {
        next.hero.x = step.x; next.hero.y = step.y;
        advanceFog(next);
        triggered.push(...checkEvents(next));
        checkResources(next);
      }
    }
    next.turn += 1;
    return { state: next, triggeredEvents: triggered };
  }

  if (decision.kind === "action") {
    next.turn += 1;
    return { state: next, triggeredEvents: triggered };
  }

  return { state: next, triggeredEvents: triggered };
}

export function advanceFog(state: WorldState): WorldState {
  const { x: hx, y: hy, visionRadius } = state.hero;
  const vrSq = visionRadius * visionRadius;
  const yMin = Math.max(0, hy - visionRadius);
  const yMax = Math.min(state.map.height - 1, hy + visionRadius);
  const xMin = Math.max(0, hx - visionRadius);
  const xMax = Math.min(state.map.width - 1, hx + visionRadius);

  for (let y = yMin; y <= yMax; y++) {
    for (let x = xMin; x <= xMax; x++) {
      const distSq = (x - hx) ** 2 + (y - hy) ** 2;
      if (distSq <= vrSq) state.map.tiles[y][x].fog.discovered = true;
    }
  }
  state.map.tiles[hy]?.[hx] && (state.map.tiles[hy][hx].fog.visited = true);
  return state;
}

function checkResources(state: WorldState): void {
  const tile = state.map.tiles[state.hero.y]?.[state.hero.x];
  if (tile?.content.kind !== "resource") return;
  const res = state.resources.byId[tile.content.resourceId];
  if (!res) return;
  const inv = state.hero.inventory;
  if (!inv.resources) inv.resources = {};
  const cur = inv.resources[res.resourceType] ?? 0;
  inv.resources[res.resourceType] = cur + res.amount;
  state.resources.collectedIds.push(res.id);
  tile.content = { kind: "empty" };
}

function checkEvents(state: WorldState): TriggeredEvent[] {
  const tile = state.map.tiles[state.hero.y]?.[state.hero.x];
  const triggered: TriggeredEvent[] = [];
  if (tile?.content.kind === "event") {
    const eventIds: string[] = (tile.content as { eventIds?: string[]; eventId?: string }).eventIds
      ?? [(tile.content as { eventId: string }).eventId];

    for (const eventId of eventIds) {
      const ev = state.events.byId[eventId];
      if (!ev || ev.triggered) continue;

      if (ev.payload.kind === "vote") {
        triggered.push({ eventId: ev.id, payload: ev.payload });
      } else {
        ev.triggered = true;
        state.events.triggeredIds.push(ev.id);
        triggered.push({ eventId: ev.id, payload: ev.payload });

        if (ev.payload.kind === "reward") {
          if (ev.payload.effect) {
            applyRewardEffect(state, ev.payload.effect);
          } else if (ev.payload.reward) {
            const reward = ev.payload.reward;
            const existing = state.hero.inventory.items.find(i => i.itemId === reward.itemId);
            if (existing) existing.qty += 1;
            else state.hero.inventory.items.push({ itemId: reward.itemId, qty: 1, description: reward.description });
          }
        }
      }
    }
  }
  return triggered;
}

import type { RewardEffect } from "./types";

export function applyRewardEffect(state: WorldState, effect: RewardEffect): void {
  const hero = state.hero;
  switch (effect.type) {
    case "resource": {
      const r = hero.inventory.resources;
      r[effect.resourceType] = (r[effect.resourceType] ?? 0) + effect.amount;
      break;
    }
    case "equipment": {
      const existing = hero.inventory.items.find(i => i.itemId === effect.equipmentId);
      if (existing) existing.qty += 1;
      else hero.inventory.items.push({ itemId: effect.equipmentId, qty: 1 });
      break;
    }
    case "hp": {
      hero.hp = Math.min(hero.hp + effect.amount, hero.maxHp);
      break;
    }
    case "stat": {
      hero.baseStats[effect.stat] = (hero.baseStats[effect.stat] ?? 0) + effect.delta;
      hero.stats[effect.stat] = (hero.stats[effect.stat] ?? 0) + effect.delta;
      break;
    }
    case "energy": {
      // Energy lives on GameRoom row (server-side); client core is no-op.
      // Server applies effect.amount through MoveResponse.energy_delta.
      break;
    }
    case "xp": {
      hero.xp = (hero.xp ?? 0) + effect.amount;
      break;
    }
  }
}

export function makeTargetOptionId(x: number, y: number): string { return `target:${x}:${y}`; }
export function parseTargetOptionId(optionId: string): { x: number; y: number } | null {
  const m = optionId.match(/^target:(\d+):(\d+)$/);
  return m ? { x: parseInt(m[1], 10), y: parseInt(m[2], 10) } : null;
}

export function canAfford(resources: WorldState["hero"]["inventory"]["resources"], cost: BuildingCost): boolean {
  if (!resources) return !cost || Object.keys(cost).length === 0;
  for (const [key, need] of Object.entries(cost)) {
    const have = resources[key as ResourceType] ?? 0;
    if (have < (need ?? 0)) return false;
  }
  return true;
}

function deductCost(resources: WorldState["hero"]["inventory"]["resources"], cost: BuildingCost): void {
  if (!resources) return;
  for (const [key, amount] of Object.entries(cost)) {
    const k = key as ResourceType;
    const cur = resources[k] ?? 0;
    const sub = amount ?? 0;
    if (cur <= sub) delete resources[k];
    else resources[k] = cur - sub;
  }
}


export function getBuildingCostForLevel(def: TownBuildingDef, currentLevel: number): BuildingCost {
  if (currentLevel === 0) return def.cost;
  if (currentLevel >= def.maxLevel) return {};
  return def.upgradeCost;
}


export function formatBuildingCost(cost: BuildingCost): string {
  if (!cost || Object.keys(cost).length === 0) return "—";
  return (Object.entries(cost) as [ResourceType, number][])
    .filter(([, qty]) => qty > 0)
    .map(([type, qty]) => `${qty} ${RESOURCE_LABELS_GENITIVE[type]}`)
    .join(", ");
}

export type BuildBuildingResult = { ok: true; state: WorldState } | { ok: false; reason: string };

export function buildBuilding(state: WorldState, townId: string, buildingId: string): BuildBuildingResult {
  const town = state.towns.byId[townId];
  if (!town) return { ok: false, reason: "Город не найден" };
  const def = TOWN_BUILDING_DEFS.find(d => d.id === buildingId);
  if (!def) return { ok: false, reason: "Неизвестная постройка" };
  const currentLevel = town.buildings?.[buildingId] ?? 0;
  if (currentLevel >= def.maxLevel) return { ok: false, reason: "Уже максимальный уровень" };

  const cost = getBuildingCostForLevel(def, currentLevel);
  const resources = state.hero.inventory.resources ?? {};
  if (!canAfford(resources, cost)) return { ok: false, reason: "Недостаточно ресурсов" };

  const next = cloneState(state);
  const nextTown = next.towns.byId[townId];
  if (!nextTown.buildings) nextTown.buildings = {};
  nextTown.buildings[buildingId] = currentLevel + 1;
  deductCost(next.hero.inventory.resources ?? {}, cost);
  next.serverTimeMs = Date.now();
  return { ok: true, state: next };
}
